import { DataError } from "../../../../core/errors/DataError";
import {
  BaseLogo,
  isPlaceholder,
} from "../../../../core/models/entities/BaseLogo";
import { Role } from "../../../../core/models/entities/Role";
import {
  detachNotRemovableReason,
  notRemovableReasonKey,
} from "../../customer-update/models/customerLogos";

/**
 * Pure helpers for "Unassign customer" in the logo list's popup (flag
 * REACT_APP_LOGO_UNASSIGN_ENABLED) - the counterpart of "Assign customer".
 * No React, no I/O - unit-tested on their own.
 */

type Translate = (key: string, options?: Record<string, unknown>) => string;

/**
 * hidden = no button at all; nav = the customer is NAV's main customer for
 * the logo (mainOrganizationId), which the API refuses to unlink (409
 * navMainCustomer) - an explanatory line instead of a doomed click;
 * available = the button.
 */
export type UnassignCustomerState = "hidden" | "nav" | "available";

/**
 * The popup's `customer` is the FIRST customer org of the logo, or its vendor
 * org when it has none (mapLogo: dto.customer || dto.vendor) - so a customer
 * of type "vendor" means "no customer": the button shows when Assign's
 * does not. The exclusions Assign has (Classification 11, name/number
 * placeholders) are shared; on top of them Unassign is STRICTER than
 * Assign: an explicit Admin/Vendor role (Assign shows for every role but
 * EndUser, an undefined role during profile load included) and never a
 * global logo (Assign does not look at isForAllCustomers). An end user
 * never sees either.
 */
export function unassignCustomerState(
  logo: BaseLogo,
  role: Role | undefined,
): UnassignCustomerState {
  if (role !== Role.Admin && role !== Role.Vendor) return "hidden";
  if (logo.isForAllCustomers) return "hidden";
  const customer = logo.customer;
  if (!customer || customer.type === "vendor") return "hidden";
  if (String(logo.Classification) === "11") return "hidden";
  if (isPlaceholder(logo)) return "hidden";
  if (
    typeof logo.mainOrganizationId === "number" &&
    logo.mainOrganizationId === customer.id
  ) {
    return "nav";
  }
  return "available";
}

/**
 * The confirm text. The popup cannot tell where the link comes from without
 * an extra GET, so it always carries the NAV-filter warning the customer
 * tab shows for links of unknown source (a marker link is simply removed;
 * a filter link comes back with the nightly sync).
 */
export function unassignConfirmText(logo: BaseLogo, t: Translate): string {
  return t("logoList.unassignCustomerConfirm", {
    customer: logo.customer?.name ?? "",
    title: logo.title ?? "",
  });
}

/**
 * The route's own 404 bodies (CustomerLogoAttachFunctions.DetachCustomerLogo):
 * "Logo {id} is not linked to organization {org}." (sub-flag
 * CustomerLogoLinkSourceEnabled on) and "Logo {id} is not manually attached
 * to organization {org}." (sub-flag off: a link that is no tab marker).
 * Both arrive as a JSON string, which errorStatusFromBody hands over as the
 * message.
 */
const NOT_LINKED_BODY = /is not (linked|manually attached) to organization/i;

/**
 * The DELETE answered 404 WITH the route's "not linked" body: the customer
 * is not (or no longer) linked as far as the API can see - removed
 * meanwhile by a colleague or the nightly sync, or (API sub-flag off) a link
 * that is no tab marker. The popup's customer is stale either way: close it
 * and refetch. A 404 WITHOUT that body is not this case: the route answers a
 * bodiless NotFoundResult while the API flag CustomerLogoAttachEnabled is
 * off, and a missing route/proxy 404 carries no such text either - the link
 * is untouched then, so the caller must neither close nor refetch nor claim
 * the link was not found (generic error toast instead).
 */
export function isUnassignNotLinked(error: unknown): boolean {
  if (!(error instanceof DataError)) return false;
  const status = error.errorStatus;
  if (!status || status.code !== 404) return false;
  return typeof status.message === "string" && NOT_LINKED_BODY.test(status.message);
}

/**
 * Error toast of a failed DELETE: a 409 notRemovable names the API's reason
 * (translated for the known codes, the generic "cannot be removed" line for
 * an envelope without one - never a raw code); a 404 with the route's "not
 * linked" body says the link was not found; everything else (a bodiless
 * 404 included) is the generic text plus the API's message when it has one
 * (the withToasts convention).
 */
export function unassignErrorText(error: unknown, t: Translate): string {
  const reason = detachNotRemovableReason(error);
  if (reason !== undefined) {
    const key = notRemovableReasonKey(reason);
    return t("logoList.customerUnassignNotRemovable", {
      reason: key ? t(key) : reason || t("customerUpdate.logosNotRemovable"),
    });
  }
  if (isUnassignNotLinked(error)) {
    return t("logoList.customerUnassignNotLinked");
  }
  const generic = t("logoList.customerUnassignFailed");
  const message = (error as { message?: unknown } | undefined)?.message;
  return typeof message === "string" && message
    ? `${generic}: ${message}`
    : generic;
}
